import type { Destination, Extension, FlowMessage, HoursCheck, IvrMenu, Queue, RingGroup } from '@arcnet/contracts';
import type { FlowNode } from './flowchart.js';

/**
 * What an inbound route does, as a flowchart tree (docs/26 D1).
 *
 * Followed from the numbers to the end of every branch: the hours decision,
 * then through hunt lists, queues and IVR menus to wherever each finally lets
 * go of the call. It answers what the form cannot: "if I ring the main line at
 * eight at night and press 2, what happens?" -- which, three menus and two
 * timeouts deep, nobody reliably works out from forms.
 *
 * Every point where the call can go more than one way is a decision: within
 * hours or not, which key, answered or not. Pure, so the picture is a function
 * of the route being edited and nothing else.
 */

export interface FlowRoute {
  numbers: string[];
  destination: Destination | null;
  /** Null when the route always sends calls the same way. */
  hours: { label: string; openNow: boolean | null } | null;
  closed: Destination | null;
  enabled: boolean;
}

export interface Lookup {
  ext: Map<string, Extension>;
  groups: Map<string, RingGroup>;
  queues: Map<string, Queue>;
  ivrs: Map<string, IvrMenu>;
  /** Call steps (docs/41 §3) and mailboxes; absent where a caller has not loaded them. */
  messages?: Map<string, FlowMessage>;
  hours?: Map<string, HoursCheck>;
  boxes?: Map<string, { extension_number: string; extension_name: string }>;
}

/** Menus that lead back into menus stop being useful to draw long before they stop. */
const MAX_DEPTH = 4;

const STRATEGY: Record<string, string> = {
  simultaneous: 'all at once', sequential: 'in order', random: 'in random order',
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const end = (title: string, detail?: string, tone?: 'warn' | 'danger', loop?: boolean): FlowNode =>
  ({ shape: 'end', title, detail, tone, loop, next: [] });

/** A ring that either connects or moves on. */
const answered = (question: string, connected: string, otherwise: FlowNode): FlowNode => ({
  shape: 'decision', title: question,
  next: [{ label: 'Yes', node: end(connected) }, { label: 'No', node: otherwise }],
});

/**
 * One destination and everything after it.
 *
 * `none` is what an empty destination means where this one sits: an inbound
 * route with nowhere to go refuses the call, while a hunt list or menu with no
 * timeout destination simply hangs up (fsxml.ts, onTimeout).
 */
function dest(
  d: Destination | null, none: 'refused' | 'hangup', depth: number, seen: ReadonlySet<string>, data: Lookup,
): FlowNode {
  if (!d) return none === 'refused' ? end('Refused', 'No destination is set.', 'danger') : end('Hangs up');
  const after = (id: string) => new Set([...seen, id]);
  const gone = (what: string) => end(`A ${what} that no longer exists`, 'The call is refused.', 'danger');

  switch (d.type) {
    case 'extension': {
      const e = data.ext.get(d.id);
      if (!e) return gone('extension');
      return end(`Rings ${e.number} · ${e.display_name}`, 'Their forwarding and voicemail apply.');
    }
    case 'ring_group': {
      const g = data.groups.get(d.id);
      if (!g) return gone('hunt list');
      if (seen.has(g.id) || depth >= MAX_DEPTH) return end(`Back to hunt list ${g.name}`, undefined, undefined, true);
      return {
        shape: 'step',
        title: `Hunt list ${g.name}`,
        detail: `Rings ${plural(g.members.length, 'member', 'members')} ${STRATEGY[g.strategy] ?? g.strategy}, `
          + `${g.ring_time_sec}s${g.strategy === 'simultaneous' ? '' : ' each'}`,
        tone: g.members.length === 0 ? 'warn' : undefined,
        next: [{
          node: answered('Answered?', 'Connected',
            dest(g.timeout_destination, 'hangup', depth + 1, after(g.id), data)),
        }],
      };
    }
    case 'queue': {
      const q = data.queues.get(d.id);
      if (!q) return gone('queue');
      if (seen.has(q.id) || depth >= MAX_DEPTH) return end(`Back to queue ${q.name}`, undefined, undefined, true);
      return {
        shape: 'step',
        title: `Queue ${q.name}`,
        detail: `${plural(q.agents.length, 'agent', 'agents')}, callers wait up to ${Math.round(q.max_wait_sec / 60)} min`,
        tone: q.agents.length === 0 ? 'warn' : undefined,
        next: [{
          node: answered('Answered in time?', 'Connected to an agent',
            dest(q.timeout_destination, 'hangup', depth + 1, after(q.id), data)),
        }],
      };
    }
    case 'ivr': {
      const m = data.ivrs.get(d.id);
      if (!m) return gone('menu');
      if (seen.has(m.id) || depth >= MAX_DEPTH) return end(`Back to menu ${m.name}`, undefined, undefined, true);
      const inner = (x: Destination | null) => dest(x, 'hangup', depth + 1, after(m.id), data);
      const keys = [...m.options].sort((a, b) => a.digit.localeCompare(b.digit));
      return {
        shape: 'step',
        title: `Menu ${m.name}`,
        detail: (m.answer_after_rings ?? 0) > 0
          ? `Rings ${plural(m.answer_after_rings ?? 0, 'time', 'times')}, then ${m.greeting_media_id ? 'plays its greeting' : 'no greeting'}, up to ${plural(m.max_attempts, 'time', 'times')}`
          : `${m.greeting_media_id ? 'Plays its greeting' : 'No greeting'}, up to ${plural(m.max_attempts, 'time', 'times')}`,
        next: [{
          node: {
            shape: 'decision',
            title: 'Which key?',
            next: [
              ...keys.map((o) => ({ label: `${o.digit}${o.label ? ` · ${o.label}` : ''}`, node: inner(o.destination) })),
              ...(m.direct_dial
                ? [{ label: 'An extension', node: end('Rings that extension', 'Internal numbers only.') }]
                : []),
              { label: 'No key', node: inner(m.timeout_destination) },
              { label: 'Wrong key', node: inner(m.invalid_destination ?? m.timeout_destination) },
            ],
          },
        }],
      };
    }
    case 'message': {
      const m = data.messages?.get(d.id);
      if (!m) return gone('message');
      if (seen.has(m.id) || depth >= MAX_DEPTH) return end(`Back to message ${m.name}`, undefined, undefined, true);
      return {
        shape: 'step',
        title: `Message ${m.name}`,
        detail: m.media_name ? `Plays ${m.media_name}` : 'No recording chosen',
        tone: m.media_id ? undefined : 'warn',
        next: [{ node: dest(m.next_destination, 'hangup', depth + 1, after(m.id), data) }],
      };
    }
    case 'hours': {
      const h = data.hours?.get(d.id);
      if (!h) return gone('hours check');
      if (seen.has(h.id) || depth >= MAX_DEPTH) return end(`Back to hours check ${h.name}`, undefined, undefined, true);
      const label = h.hours.mode === 'own' ? 'Own hours' : h.hours.schedule_name ?? 'A schedule';
      return {
        shape: 'decision',
        title: `${h.name}: within hours?`,
        detail: h.hours.open_now === null ? label : `${label}, ${h.hours.open_now ? 'open now' : 'closed now'}`,
        next: [
          { label: 'Open', node: dest(h.open_destination, 'hangup', depth + 1, after(h.id), data) },
          { label: 'Closed', node: dest(h.closed_destination, 'hangup', depth + 1, after(h.id), data) },
        ],
      };
    }
    case 'voicemail': {
      const b = data.boxes?.get(d.id);
      return end(b ? `Voicemail · ${b.extension_number} ${b.extension_name}` : 'Voicemail', 'The caller leaves a message.');
    }
    case 'external':
      return end(`Forwards to ${d.number}`, "Over the account's outbound routing.");
    case 'hangup':
      return end('Hangs up');
    default:
      return end('Unknown destination', undefined, 'danger');
  }
}

export function buildRouteTree(route: FlowRoute, data: Lookup): FlowNode {
  const seen = new Set<string>();
  const hasNumbers = route.numbers.length > 0;
  const first: FlowNode = !route.enabled
    ? end('Refused', 'The route is switched off.', 'warn')
    : route.hours
      ? {
          shape: 'decision',
          title: 'Within hours?',
          detail: route.hours.openNow === null
            ? route.hours.label
            : `${route.hours.label}, ${route.hours.openNow ? 'open now' : 'closed now'}`,
          next: [
            { label: 'Open', node: dest(route.destination, 'refused', 0, seen, data) },
            { label: 'Closed', node: dest(route.closed, 'refused', 0, seen, data) },
          ],
        }
      : dest(route.destination, 'refused', 0, seen, data);

  return {
    shape: 'start',
    title: hasNumbers ? `Call to ${route.numbers.join(', ')}` : 'No numbers yet',
    detail: hasNumbers ? undefined : 'Assign a number and calls to it follow this route.',
    tone: hasNumbers ? undefined : 'warn',
    next: [{ node: first }],
  };
}
